import { CYCLE_TIMELINE_LAYOUT, Card, Heading, PageContainer, Text } from '@amelio/ui-library';

import { DeadlineMilestoneId } from '../../milestones';
import { CycleRecurrence } from '../CycleRecurrence/CycleRecurrence';
import { MilestoneCard } from '../MilestoneCard/MilestoneCard';

import type { DeadlineMilestoneIdValue, NumberedDeadlineMilestone } from '../../milestones';
import type { DeadlineErrorsById, DeadlineMilestoneValues } from '../../validation';
import type { MilestoneReminderValues } from '../ReminderPills/ReminderPills';
import type { FrequencyValue } from '@/constants/Frequency';

import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { StepFooter } from '@/navigation/shared/StepFooter';
import {
    CYCLE_CARD_SURFACE,
    CYCLE_CARD_SURFACE_BG_COLOR,
    CYCLE_CARD_SURFACE_BORDER_COLOR,
} from '@/pages/CycleEvaluation/components/cycleCardSurface';

export interface DeadlinesProps {
    isLoading: boolean;
    isReadOnly?: boolean;
    numberedMilestones: NumberedDeadlineMilestone[];
    values: DeadlineMilestoneValues;
    remindersById: Partial<Record<DeadlineMilestoneIdValue, MilestoneReminderValues>>;
    errors: DeadlineErrorsById;
    frequency?: FrequencyValue;
    recurrenceErrorMessage?: string;
    onScheduledAtChange: (id: DeadlineMilestoneIdValue, date?: Date) => void;
    onDueAtChange: (id: DeadlineMilestoneIdValue, date?: Date) => void;
    onRemindersChange: (id: DeadlineMilestoneIdValue, values: MilestoneReminderValues) => void;
    onFrequencyChange: (frequency: FrequencyValue) => void;
}

export const Deadlines = ({
    isLoading,
    isReadOnly = false,
    numberedMilestones,
    values,
    remindersById,
    errors,
    frequency,
    recurrenceErrorMessage,
    onScheduledAtChange,
    onDueAtChange,
    onRemindersChange,
    onFrequencyChange,
}: DeadlinesProps) => {
    const { t } = useAmelioTranslations();

    const recurrenceField = (
        <CycleRecurrence
            frequency={frequency}
            errorMessage={recurrenceErrorMessage}
            isReadOnly={isReadOnly}
            onChange={onFrequencyChange}
        />
    );

    return (
        <PageContainer className={'cycle-evaluation-group-section'}>
            <Card
                padding={'none'}
                className={CYCLE_CARD_SURFACE}
                bgColor={CYCLE_CARD_SURFACE_BG_COLOR}
                borderColor={CYCLE_CARD_SURFACE_BORDER_COLOR}
            >
                <div>
                    <Heading level={4}>{t('cycleEvaluationDeadlines.header.heading')}</Heading>

                    <Text className={'mt-3'}>
                        {t('cycleEvaluationDeadlines.header.description')}
                    </Text>
                </div>

                {isLoading ? (
                    <Skeleton className={`mt-9 flex flex-col ${CYCLE_TIMELINE_LAYOUT.gap}`}>
                        <Bone className={'h-28 w-full'} />

                        <Bone className={'h-28 w-full'} />

                        <Bone className={'h-28 w-full'} />

                        <Bone className={'h-28 w-full'} />
                    </Skeleton>
                ) : (
                    <div className={'relative mt-9'}>
                        <div
                            className={[
                                'absolute top-0 bottom-0',
                                CYCLE_TIMELINE_LAYOUT.railLeft,
                                CYCLE_TIMELINE_LAYOUT.railThickness,
                                'bg-linear-to-b from-[var(--color-cycle-timeline-rail)] to-transparent',
                            ].join(' ')}
                            aria-hidden={'true'}
                            data-testid={'milestone-timeline-rail'}
                        />

                        <div className={`flex flex-col ${CYCLE_TIMELINE_LAYOUT.gap}`}>
                            {numberedMilestones.map((numbered, index) => (
                                <MilestoneCard
                                    key={numbered.milestone.id}
                                    numbered={numbered}
                                    value={values[numbered.milestone.id]}
                                    reminders={remindersById[numbered.milestone.id]}
                                    errors={errors[numbered.milestone.id]}
                                    isReadOnly={isReadOnly}
                                    zIndex={numberedMilestones.length - index}
                                    sideField={
                                        numbered.milestone.id ===
                                        DeadlineMilestoneId.PROJECT_KICK_OFF
                                            ? recurrenceField
                                            : undefined
                                    }
                                    onScheduledAtChange={(date) =>
                                        onScheduledAtChange(numbered.milestone.id, date)
                                    }
                                    onDueAtChange={(date) =>
                                        onDueAtChange(numbered.milestone.id, date)
                                    }
                                    onRemindersChange={(reminderValues) =>
                                        onRemindersChange(numbered.milestone.id, reminderValues)
                                    }
                                />
                            ))}
                        </div>
                    </div>
                )}

                <StepFooter />
            </Card>
        </PageContainer>
    );
};
